/**
 * Keeps VR players inside the spaces they're in: no teleporting, gliding or
 * walking (room-scale) through walls. A step is blocked when a straight line
 * at chest height, from where you are to where you'd be, crosses geometry; a
 * teleport only when the line above your head is blocked too (see jumpBlocked).
 *
 * Raycasts respect each material's side, so a one-sided wall stops you going
 * out through its face but lets someone stranded outside come back in.
 */
import * as THREE from 'three';

/** Chest height above the landing point (below doorway tops, above tables and railings). */
export const CHEST = 1.2;

const _ray = new THREE.Raycaster();
const _d = new THREE.Vector3();
const _n = new THREE.Vector3();
const _nm = new THREE.Matrix3();
const PROXY = /^(collider|ucx|collision)[_.]/i;

/** Is the straight path from `from` to `to` (both at chest height) cut by any of `meshes`? */
export function pathBlocked(meshes: THREE.Object3D[], from: THREE.Vector3, to: THREE.Vector3, margin = 0): boolean {
  _d.copy(to).sub(from);
  const len = _d.length();
  if (len < 1e-3 || meshes.length === 0) return false;
  _ray.set(from, _d.multiplyScalar(1 / len));
  _ray.near = 0;
  _ray.far = len + margin;
  for (const hit of _ray.intersectObjects(meshes, false)) {
    // Collision boxes are closed volumes: only their outside stops you, so a
    // player who ends up inside one (standing up from a sofa) can step out.
    if (PROXY.test(hit.object.name) && hit.face) {
      _n.copy(hit.face.normal).applyMatrix3(_nm.getNormalMatrix(hit.object.matrixWorld));
      if (_n.dot(_d) > 0) continue;
    }
    return true;
  }
  return false;
}

/** Head clearance above chest height for the over-the-railing test (chest + 0.8 ≈ just above your head). */
const OVERHEAD = 0.8;
const _a = new THREE.Vector3(), _b = new THREE.Vector3();
/**
 * A teleport is blocked only by a real wall. If the straight chest-height line
 * is cut, the jump may still "hop": rise straight up to just above the higher
 * head, cross over, and drop down — which clears railings and balustrades (up
 * onto a mezzanine) but not walls, and not a ceiling over your head.
 */
export function jumpBlocked(meshes: THREE.Object3D[], fromChest: THREE.Vector3, toChest: THREE.Vector3): boolean {
  if (!pathBlocked(meshes, fromChest, toChest)) return false;
  const top = Math.max(fromChest.y, toChest.y) + OVERHEAD;
  _a.set(fromChest.x, top, fromChest.z); _b.set(toChest.x, top, toChest.z);
  return pathBlocked(meshes, fromChest, _a) || pathBlocked(meshes, _a, _b) || pathBlocked(meshes, _b, toChest);
}
/** Where the player was last tick (chest, world space). Cleared when the game moves them on purpose (spawn, back to start). */
export const vrPath = {
  prev: null as THREE.Vector3 | null,
  reset() { this.prev = null; },
};
if (import.meta.env.DEV) (window as any).__gxPathReset = () => vrPath.reset();

/**
 * Furniture proxies (`collider_furniture_*`): pods, tables, lecterns,
 * planters. Unlike floors and stages you can't stand on them — VR stick and
 * room-scale walking are stopped at knee height (the chest-height wall test
 * passes over a 0.9 m table) and teleports can't land on them.
 */
const FURNITURE = /^collider_furniture/i;
export const isFurniture = (o: THREE.Object3D) => FURNITURE.test(o.name);
/** Knee height above the feet: over any step you can climb (0.6 m), under tables and seats. */
export const KNEE = 0.7;

/** World boxes of every furniture proxy under `root`. */
export function furnitureBoxes(root: THREE.Object3D): THREE.Box3[] {
  const out: THREE.Box3[] = [];
  root.traverse((o) => { if ((o as THREE.Mesh).isMesh && isFurniture(o)) out.push(new THREE.Box3().setFromObject(o)); });
  return out;
}

/** Is (x, z) on top of / inside a piece of furniture (with `pad` metres of clearance)? */
export function onFurniture(boxes: THREE.Box3[], x: number, z: number, pad = 0.2): boolean {
  return boxes.some((b) => x > b.min.x - pad && x < b.max.x + pad && z > b.min.z - pad && z < b.max.z + pad);
}
